import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * OS-level personalisation: accent colour and wallpaper, the way Windows
 * Settings owns them. Persisted to localStorage like real user preferences.
 */

export const ACCENTS = [
  { name: "Windows blue", value: "#0078d4" },
  { name: "Navy", value: "#0f4c81" },
  { name: "Teal", value: "#00838f" },
  { name: "Seafoam", value: "#00825f" },
  { name: "Green", value: "#3a8a3a" },
  { name: "Gold", value: "#b8860b" },
  { name: "Orange", value: "#c85200" },
  { name: "Brick", value: "#b3352d" },
  { name: "Rose", value: "#b0356b" },
  { name: "Violet", value: "#6b3fa0" },
  { name: "Iris", value: "#4b4bb3" },
  { name: "Steel", value: "#4a5b6b" },
];

export const WALLPAPERS = [
  {
    id: "bloom",
    name: "Bloom",
    petals: true,
    light:
      "radial-gradient(120% 120% at 20% 0%, #7db8ff 0%, #4a8ce8 34%, #2b5fc4 68%, #17357e 100%)",
    dark: "radial-gradient(120% 120% at 20% 0%, #2a6ff0 0%, #1b3ea8 38%, #0b1a4a 72%, #050b22 100%)",
  },
  {
    id: "glow",
    name: "Glow",
    petals: false,
    light:
      "linear-gradient(160deg, #ffd6a5 0%, #ff8fab 42%, #a06cd5 78%, #5a3a9e 100%)",
    dark: "linear-gradient(160deg, #3b1f47 0%, #7a2e5c 45%, #b8455c 78%, #d97742 100%)",
  },
  {
    id: "flow",
    name: "Flow",
    petals: false,
    light:
      "linear-gradient(140deg, #d5f5e3 0%, #76d7c4 40%, #2e86c1 80%, #1b4f72 100%)",
    dark: "linear-gradient(140deg, #04262b 0%, #0b5563 42%, #12868c 76%, #29c7b0 100%)",
  },
  {
    id: "sunrise",
    name: "Sunrise",
    petals: false,
    light:
      "linear-gradient(200deg, #fff3b0 0%, #ffb703 38%, #fb8500 68%, #d62828 100%)",
    dark: "linear-gradient(200deg, #1a1423 0%, #3d2645 38%, #8b3a3a 72%, #e07a5f 100%)",
  },
  {
    id: "graphite",
    name: "Graphite",
    petals: false,
    light: "linear-gradient(150deg, #e6e9ef 0%, #b8c0cc 50%, #7d8899 100%)",
    dark: "linear-gradient(150deg, #1c1f26 0%, #2b313c 50%, #10131a 100%)",
  },
];

const SystemContext = createContext(null);

export const useSystem = () => {
  const ctx = useContext(SystemContext);
  if (!ctx) throw new Error("useSystem must be used inside <SystemProvider>");
  return ctx;
};

const read = (key, fallback) => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
};

export function SystemProvider({ children }) {
  const [accent, setAccent] = useState(() => read("win11-accent", "#0078d4"));
  const [wallpaperId, setWallpaperId] = useState(() =>
    read("win11-wallpaper", "bloom")
  );

  const wallpaper =
    WALLPAPERS.find((w) => w.id === wallpaperId) || WALLPAPERS[0];

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--accent", accent);
    root.style.setProperty("--accent-hover", `color-mix(in srgb, ${accent} 82%, #fff)`);
    root.style.setProperty("--accent-soft", `color-mix(in srgb, ${accent} 14%, transparent)`);
    try {
      localStorage.setItem("win11-accent", accent);
    } catch {
      /* ignore */
    }
  }, [accent]);

  useEffect(() => {
    try {
      localStorage.setItem("win11-wallpaper", wallpaperId);
    } catch {
      /* ignore */
    }
  }, [wallpaperId]);

  const value = useMemo(
    () => ({ accent, setAccent, wallpaper, setWallpaper: setWallpaperId }),
    [accent, wallpaper]
  );

  return (
    <SystemContext.Provider value={value}>{children}</SystemContext.Provider>
  );
}
